// src/lib/metrics/derivatives.ts
/**
 * Derivatives metrics - calculations for funding rates and open interest
 * 
 * Pure functions for computing derivatives-related metrics.
 * Used by TokenDerivativesProvider and derivatives components.
 */

import { stdDev, filterValid, percentPositive } from './primitives';

// ============================================================================
// FUNDING RATE METRICS
// ============================================================================

/**
 * Calculate cumulative funding rate over a period
 * Assumes ~3 funding periods per day (8-hour intervals)
 * 
 * @param rates - Array of funding rate values
 * @param periodsPerDay - Number of funding periods per day (default 3)
 * @returns Cumulative rate adjusted for daily periods, or null if no data
 */
export function cumulativeFundingRate(
  rates: (number | null | undefined)[],
  periodsPerDay: number = 3
): number | null {
  const valid = filterValid(rates);
  if (valid.length === 0) return null;
  
  const sum = valid.reduce((a, b) => a + b, 0);
  return sum * periodsPerDay;
}

/**
 * Calculate funding rate volatility as standard deviation * 100
 * 
 * @param rates - Array of funding rate values
 * @returns Volatility as percentage, or null if insufficient data
 */
export function fundingRateVolatility(
  rates: (number | null | undefined)[]
): number | null {
  const valid = filterValid(rates);
  if (valid.length < 2) return null;
  
  const vol = stdDev(valid);
  return vol !== null ? vol * 100 : null;
}

/**
 * Calculate percentage of positive funding rates
 * 
 * @param rates - Array of funding rate values
 * @returns Percentage (0-100) of positive rates, or null if no data
 */
export function fundingRatePercentPositive(
  rates: (number | null | undefined)[]
): number | null {
  return percentPositive(rates);
}

/**
 * Compute all funding stats for a window
 */
export interface FundingStats {
  cumulative: number | null;
  volatility: number | null;
  percentPositive: number | null;
}

export function computeFundingStats(
  rates: (number | null | undefined)[]
): FundingStats {
  return {
    cumulative: cumulativeFundingRate(rates),
    volatility: fundingRateVolatility(rates),
    percentPositive: fundingRatePercentPositive(rates),
  };
}

// ============================================================================
// OPEN INTEREST METRICS
// ============================================================================

/**
 * Calculate percentage change in open interest
 * 
 * @param values - Array of OI values (chronological)
 * @param days - Number of days to look back
 * @returns Percentage change, or null if insufficient data
 */
export function oiChangePct(
  values: (number | null | undefined)[],
  days: number
): number | null {
  const valid = filterValid(values);
  if (valid.length < 2) return null;
  
  const pointsNeeded = days + 1;
  if (valid.length < pointsNeeded) return null;
  
  const latest = valid[valid.length - 1];
  const past = valid[valid.length - pointsNeeded];
  
  if (latest === undefined || past === undefined || past === 0) return null;
  
  return ((latest - past) / past) * 100;
}

// ============================================================================
// REGIME DETECTION
// ============================================================================

export type MarketRegime = 'Leverage build' | 'De-leveraging' | 'Mixed';

/**
 * Determine market regime based on OI change and funding direction
 * 
 * @param oiChange7dPct - 7-day OI change percentage
 * @param fundingSum - Sum of funding rates (or cumulative)
 * @returns Market regime classification
 */
export function determineRegime(
  oiChange7dPct: number | null,
  fundingSum: number | null
): MarketRegime {
  if (oiChange7dPct === null || fundingSum === null) return 'Mixed';
  if (oiChange7dPct > 0 && fundingSum > 0) return 'Leverage build';
  if (oiChange7dPct < 0 && fundingSum < 0) return 'De-leveraging';
  return 'Mixed';
}

// ============================================================================
// EXTREMES
// ============================================================================

export interface FundingExtreme {
  timestamp: number;
  rate: number;
}

export interface FundingExtremes {
  top: FundingExtreme[];
  bottom: FundingExtreme[];
}

/**
 * Find top and bottom N funding rate extremes from a series
 * 
 * @param series - Array of {timestamp, rate} objects
 * @param topN - Number of extremes to return (default 3)
 * @returns Object with top and bottom arrays
 */
export function findFundingExtremes(
  series: Array<{ timestamp: number; rate: number }>,
  topN: number = 3
): FundingExtremes {
  if (series.length === 0) {
    return { top: [], bottom: [] };
  }
  
  const valid = series.filter(p => Number.isFinite(p.rate));
  const sorted = [...valid].sort((a, b) => a.rate - b.rate);
  
  const bottom = sorted
    .slice(0, Math.min(topN, sorted.length))
    .map(p => ({ timestamp: p.timestamp, rate: p.rate }));
  
  const top = sorted
    .slice(-Math.min(topN, sorted.length))
    .reverse()
    .map(p => ({ timestamp: p.timestamp, rate: p.rate }));
  
  return { top, bottom };
}

// ============================================================================
// WINDOW SLICING
// ============================================================================

/**
 * Slice a funding rate series by window days
 * Note: Weighted funding rate data from API is DAILY (1 point per day)
 * 
 * @param series - Array of funding rate points (daily)
 * @param windowDays - Number of days to include
 */
export function sliceFundingByWindow<T>(
  series: T[],
  windowDays: number
): T[] {
  return series.slice(-windowDays);
}

